import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";

import { fetchAbsenceTypes } from "../../../api/absence/type";
import { fetchUserAbsenceStats } from "../../../api/user";
import {
  BottomSheet,
  rowButtonStyle,
  PickerCheckIcon,
  EmptyRow,
  LoadingRow,
} from "../hours/hourFormPickers";

const absenceTypeLabel = (t) => t?.displayTitle ?? t?.title ?? `#${t?.id}`;

/**
 * Absence-type picker — mobile-first bottom sheet matching the hour_form.js
 * pickers (reuses the shared BottomSheet + row styling). Fetches the company's
 * absence types with debounced search and shows the employee's remaining
 * balance per type as a subtitle. Returns the full absence-type object via
 * onSelect so the form keeps `code` / `max_duration_days` for validation.
 */
export const AbsenceTypePickerSheet = ({ open, onClose, userId, selectedId, onSelect }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchRows = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = {
        page: 1,
        per_page: 50,
        sortBy: "title",
        sort: "asc",
        fields: "id,title,displayTitle,code,max_duration_days,max_occurrences_per_year,description",
      };
      if (q && q.trim()) params.query = q.trim();
      const response = await fetchAbsenceTypes(params);
      const data = Array.isArray(response?.data) ? response.data : response?.data?.data || [];
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debounced = useRef(debounce((q) => fetchRows(q), 300)).current;
  useEffect(() => {
    if (!open) return undefined;
    debounced(query);
    return () => debounced.cancel();
  }, [query, open, debounced]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    if (!userId) {
      setStats(null);
      return;
    }
    let cancelled = false;
    const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().split("T")[0];
    const yearEnd = new Date().toISOString().split("T")[0];
    fetchUserAbsenceStats(userId, { from: yearStart, to: yearEnd })
      .then((response) => {
        if (!cancelled && response?.status === 200) setStats(response.data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  const subtitleFor = (type) => {
    const s = stats?.[type?.code];
    if (s && !s.error) {
      if (type?.code === "compensatory_time_off" && s.earned_hours_left != null) {
        return t("absence.edit.type_hours_left", { hours: s.earned_hours_left || 0 });
      }
      if (s.remaining_days != null || s.used_days != null) {
        return t("absence.edit.type_balance", { used: s.used_days || 0, remaining: s.remaining_days || 0 });
      }
    }
    return type?.description || "";
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t("absence.edit.select_absence_type")}
      searchValue={query}
      onSearch={setQuery}
      searchPlaceholder={t("absence.edit.type_search")}
    >
      {loading ? (
        <LoadingRow text={t("absence.edit.loading")} />
      ) : rows.length === 0 ? (
        <EmptyRow text={t("absence.edit.type_empty")} />
      ) : (
        rows.map((type) => {
          const isSel = String(type.id) === String(selectedId);
          const subtitle = subtitleFor(type);
          return (
            <button
              key={type.id}
              type="button"
              style={{ ...rowButtonStyle, alignItems: "flex-start", padding: "14px 12px" }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(type);
                onClose();
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                  {absenceTypeLabel(type)}
                </span>
                {subtitle ? (
                  <span style={{ display: "block", fontSize: 13, color: "#94A3B8", fontWeight: 500, marginTop: 2, lineHeight: 1.4 }}>
                    {subtitle}
                  </span>
                ) : null}
              </span>
              {isSel && (
                <span style={{ flex: "0 0 auto", marginTop: 1 }}>
                  <PickerCheckIcon />
                </span>
              )}
            </button>
          );
        })
      )}
    </BottomSheet>
  );
};

export default AbsenceTypePickerSheet;
